import React, { useState, useRef, useEffect, forwardRef } from "react";
import { useFormContext } from "react-hook-form";

interface DateInputProps {
  value?: string;
  isEditing?: boolean;
  name: string;
  minDate?: string;
  disabled?: boolean;
  maxDate?: string;
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  ({ value = "", isEditing = true, name, minDate, disabled, maxDate }, ref) => {
    const { trigger, setValue } = useFormContext();
    const [displayValue, setDisplayValue] = useState("");
    const datePickerRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    const toDisplayFormat = (date: string) => {
      const parts = date.split("-");
      return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : "";
    };

    const toStoredFormat = (date: string) => {
      const parts = date.split(".");
      return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : "";
    };

    const formatDate = (input: string) => {
      const cleaned = input.replace(/\D+/g, "").slice(0, 8);
      const parts = [];
      if (cleaned.length > 0) parts.push(cleaned.slice(0, 2));
      if (cleaned.length > 2) parts.push(cleaned.slice(2, 4));
      if (cleaned.length > 4) parts.push(cleaned.slice(4, 8));
      return parts.join(".");
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatDate(e.target.value);
      setDisplayValue(formatted);
      setHasInteracted(true);

      const storedFormat = toStoredFormat(formatted);
      if (formatted === "") {
        setValue(name, "");
        trigger(name);
        return;
      }

      if (storedFormat.length === 10) {
        setValue(name, storedFormat, { shouldValidate: true });
        trigger(name);
      }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.value) {
        const formattedDate = toDisplayFormat(e.target.value);
        setDisplayValue(formattedDate);
        setValue(name, e.target.value, { shouldValidate: true });
        trigger(name);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      setHasInteracted(true);
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (!displayValue.trim()) {
        setValue(name, "");
        if (hasInteracted) {
          trigger(name);
        }
      }
    };

    useEffect(() => {
      if (value) {
        setDisplayValue(toDisplayFormat(value));
        if (datePickerRef.current) {
          datePickerRef.current.value = value;
        }
      }
    }, [value]);

    return (
      <div
        className={`relative w-full text-sm 2xl:text-base h-12 border flex justify-center items-center rounded-md ${
          disabled && "bg-[#fafafa]"
        } ${isFocused ? "border-blue-500" : "border-gray-300"} `}
      >
        {disabled && (
          <div className="w-full h-full absolute top-0 left-0 z-[11]"></div>
        )}

        <div className="absolute top-0 left-0 z-10 w-full">
          <div className="pr-10 px-4 py-3">
            <input
              type="text"
              value={displayValue}
              onChange={handleInputChange}
              maxLength={10}
              className={`w-full h-full rounded outline-none bg-transparent ${
                isEditing
                  ? "border-gray-300 focus:outline-none focus:border-blue-500"
                  : "border-gray-300 text-gray-400 cursor-not-allowed"
              }`}
              placeholder="dd.mm.yyyy"
              onFocus={handleFocus}
              onBlur={handleBlur}
              readOnly={!isEditing}
              ref={ref} // ✅ Attach the forwarded ref
            />
          </div>
        </div>

        <input
          type="date"
          ref={datePickerRef}
          className="absolute top-0 left-0 w-full h-full opacity-0"
          onChange={handleDateChange}
          disabled={!isEditing}
          min={minDate}
          max={maxDate}
        />

        <span
          className="absolute right-3 top-3 cursor-pointer z-10"
          onClick={() => datePickerRef.current?.showPicker()}
        >
          <CalendarIcon />
        </span>
      </div>
    );
  }
);

export default DateInput;

const CalendarIcon = () => (
  <svg
    role="img"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    className="w-5 h-5 text-gray-500"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M8 2v2M16 2v2M3 8h18M4 6h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0zM8 12h4M8 16h8"
    />
  </svg>
);
